import React, { useState, useEffect } from 'react';
import { Building2, Palette, Save, Upload, Loader, ArrowLeft } from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';
import ColorPaletteGenerator from '../components/Settings/ColorPaletteGenerator';
import { ColorPalette } from '../utils/colorPaletteGenerator';
import clientBrandingAPI from '../services/clientBrandingAPI';
import toast from 'react-hot-toast';

interface ClientBranding {
  companyName: string;
  logo: string | null;
  colorPalette: ColorPalette | null;
}

interface BrandingSettingsProps {
  projectId?: number; // Optional prop, can also come from route params
}

const BrandingSettings: React.FC<BrandingSettingsProps> = ({ projectId: propProjectId }) => {
  const { projectId: routeProjectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const projectId = propProjectId || parseInt(routeProjectId || '0');

  const [branding, setBranding] = useState<ClientBranding>({
    companyName: '',
    logo: null,
    colorPalette: null
  });

  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Load existing branding on mount
  useEffect(() => {
    const loadBranding = async () => {
      if (!projectId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const result = await clientBrandingAPI.getBranding(projectId);

        if (result.success && result.data) {
          setBranding({
            companyName: result.data.company_name,
            logo: result.data.logo,
            colorPalette: result.data.color_palette
          });

          if (result.data.logo) {
            setLogoPreview(result.data.logo);
          }

          toast.success('Branding loaded successfully');
        }
      } catch (error: any) {
        console.error('Error loading branding:', error);
        // Don't show error toast - it's ok if no branding exists yet
      } finally {
        setLoading(false);
      }
    };

    loadBranding();
  }, [projectId]);

  // Handle logo upload
  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error('Please upload an image file');
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Logo file size must be less than 5MB');
        return;
      }

      // Read file as base64
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result as string;
        setLogoPreview(base64String);
        setBranding(prev => ({ ...prev, logo: base64String }));
        toast.success('Logo uploaded successfully!');
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle palette save
  const handleSavePalette = (palette: ColorPalette) => {
    setBranding(prev => ({ ...prev, colorPalette: palette }));
  };

  // Save all branding settings
  const handleSaveBranding = async () => {
    if (!projectId) {
      toast.error('Project ID is required');
      return;
    }

    if (!branding.companyName) {
      toast.error('Please enter company name');
      return;
    }

    if (!branding.colorPalette) {
      toast.error('Please generate and save a color palette');
      return;
    }

    try {
      setSaving(true);
      toast.loading('Saving branding settings...', { id: 'save-branding' });

      await clientBrandingAPI.saveBranding(projectId, {
        companyName: branding.companyName,
        logo: branding.logo,
        colorPalette: branding.colorPalette,
        primaryColor: branding.colorPalette.primary,
        secondaryColor: branding.colorPalette.secondary,
        accentColor: branding.colorPalette.accent
      });

      toast.dismiss('save-branding');
      toast.success('Branding settings saved successfully!');

      // Navigate back to process optimization after successful save
      setTimeout(() => {
        navigate('/app/process-optimization');
      }, 1000);
    } catch (error: any) {
      toast.dismiss('save-branding');
      toast.error(error.message || 'Failed to save branding');
    } finally {
      setSaving(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6 flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-12 h-12 text-blue-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading branding settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6">
      <div className="max-w-7xl mx-auto space-y-4">
        {/* Back Button */}
        <button
          onClick={() => navigate('/app/process-optimization')}
          className="flex items-center gap-1 text-gray-600 hover:text-blue-600 transition-colors group"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
          <span className="text-sm font-medium">Back</span>
        </button>

        {/* Page Header */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center gap-4">
            <div className="bg-gradient-to-br from-blue-500 to-purple-500 p-4 rounded-lg">
              <Building2 className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-800">Client Branding Settings</h1>
              <p className="text-gray-600 mt-1">
                Configure your client's brand colors and logo for professional reports
              </p>
            </div>
          </div>
        </div>

        {/* Company Info Section */}
        <div className="bg-white rounded-lg shadow-md p-6 space-y-4">
          <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            Company Information
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Company Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Company Name *
              </label>
              <input
                type="text"
                value={branding.companyName}
                onChange={(e) => setBranding(prev => ({ ...prev, companyName: e.target.value }))}
                placeholder="Enter company name"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Logo Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Company Logo
              </label>
              <div className="flex items-center gap-4">
                {logoPreview && (
                  <div className="h-16 w-16 rounded-lg border-2 border-gray-300 overflow-hidden bg-white p-2">
                    <img
                      src={logoPreview}
                      alt="Logo preview"
                      className="w-full h-full object-contain"
                    />
                  </div>
                )}
                <label className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-white border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 cursor-pointer transition-colors">
                  <Upload className="w-5 h-5 text-gray-600" />
                  <span className="text-sm text-gray-700">
                    {logoPreview ? 'Change Logo' : 'Upload Logo'}
                  </span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="hidden"
                  />
                </label>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                PNG, JPG, or SVG (max 5MB). Recommended: 500x500px
              </p>
            </div>
          </div>
        </div>

        {/* Color Palette Generator */}
        <ColorPaletteGenerator
          onSavePalette={handleSavePalette}
          initialColor="#3B82F6"
        />

        {/* Current Branding Summary */}
        {branding.colorPalette && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Current Branding Summary</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-1">Company Name</p>
                <p className="font-semibold text-gray-800">
                  {branding.companyName || 'Not set'}
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-1">Logo</p>
                <p className="font-semibold text-gray-800">
                  {branding.logo ? '✅ Uploaded' : '❌ Not uploaded'}
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-1">Color Palette</p>
                <div className="flex gap-2 mt-2">
                  <div
                    className="w-8 h-8 rounded border border-gray-300"
                    style={{ backgroundColor: branding.colorPalette.primary }}
                    title="Primary"
                  />
                  <div
                    className="w-8 h-8 rounded border border-gray-300"
                    style={{ backgroundColor: branding.colorPalette.secondary }}
                    title="Secondary"
                  />
                  <div
                    className="w-8 h-8 rounded border border-gray-300"
                    style={{ backgroundColor: branding.colorPalette.accent }}
                    title="Accent"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            onClick={handleSaveBranding}
            disabled={saving}
            className={`flex items-center gap-2 px-8 py-3 rounded-lg transition-all shadow-lg hover:shadow-xl font-semibold ${
              saving
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:from-blue-700 hover:to-blue-800'
            }`}
          >
            {saving ? (
              <>
                <Loader className="w-5 h-5 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-5 h-5" />
                Save Branding Settings
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BrandingSettings;
